import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';

import { adminGuard } from './core/guards/admin.guard';
import { authGuard } from './core/guards/auth.guard';
import { authenticatedGuard } from './core/guards/authenticated.guard';
import { guestGuard } from './core/guards/guest.guard';
import { opsGuard } from './core/guards/ops.guard';
import { LandingComponent } from './features/auth/landing/landing';
import { LoginComponent } from './features/auth/login/login';
import { RegisterComponent } from './features/auth/register/register';
import { CheckEmailComponent } from './features/auth/check-email/check-email';
import { VerifyEmailComponent } from './features/auth/verify-email/verify-email';
import { CompleteProfileComponent } from './features/auth/complete-profile/complete-profile';
import { ResetPasswordComponent } from './features/auth/reset-password/reset-password';
import { ResetPasswordEmailSentComponent } from './features/auth/reset-password/email-sent/email-sent';
import { ResetPasswordConfirmComponent } from './features/auth/reset-password/confirm/confirm';
import { DashboardComponent } from './features/dashboard/dashboard';
import { ProfileComponent } from './features/profile/profile';
import { AgentDashboardComponent } from './features/agent/dashboard/agent-dashboard';
import { AgentOnboardClientComponent } from './features/agent/onboard-client/onboard-client';
import { AgentApplicationDetailComponent } from './features/agent/application-detail/agent-application-detail';
import { AcceptInvitationComponent } from './features/invitations/accept/accept-invitation';
import { AcceptAgencyInvitationComponent } from './features/agency-invitations/accept/accept-agency-invitation';
import { AdminAgenciesComponent } from './features/admin/agencies/admin-agencies';
import { AgentTeamComponent } from './features/agent/team/agent-team';
import { AdminDashboardComponent } from './features/admin/dashboard/admin-dashboard';
import { AdminUsersComponent } from './features/admin/users/admin-users';
import { AiEvaluationsComponent } from './features/admin/ai-evaluations/ai-evaluations';
import { AiEvaluationDetailComponent } from './features/admin/ai-evaluations/ai-evaluation-detail';
import { AdminEmailsComponent } from './features/admin/emails/emails';
import { EmailApprovalsComponent } from './features/admin/email-approvals/email-approvals';
import { AdminFeedbackComponent } from './features/admin/feedback/admin-feedback';
import { FeedbackDetailComponent } from './features/admin/feedback/feedback-detail';
import { SystemStatusComponent } from './features/admin/system-status/system-status';
import { SystemLogsComponent } from './features/admin/system-logs/system-logs';
import { DatabaseBrowserComponent } from './features/admin/database-browser/database-browser';
import { DeveloperDocsComponent } from './features/admin/developer-docs/developer-docs';
import { ComplianceComponent } from './features/admin/compliance/compliance';
import { UserJourneyComponent } from './features/admin/user-journey/user-journey';
import { CreateEntityComponent } from './features/entity/create/create-entity';
import { EntityDashboardComponent } from './features/entity/dashboard/entity-dashboard';
import { EntityTeamComponent } from './features/entity/team/entity-team';
import { DeletedBusinessesComponent } from './features/entity/deleted/deleted-businesses';
import { ProductChooserComponent } from './features/entity/applications/product-chooser/product-chooser';
import { PreApplicationViewComponent } from './features/entity/applications/submission/pre-application-view';
import { directApplicationGuard } from './core/applications/direct-applications';
import { AddDocumentsComponent } from './features/entity/applications/add-documents/add-documents';
// Kickstart Cash — entity screens
import { KsListComponent } from './features/entity/kickstart/list/ks-list';
import { KsCreateComponent } from './features/entity/kickstart/create/ks-create';
import { KsDetailComponent } from './features/entity/kickstart/detail/ks-detail';
// Debtors Finance — entity screens
import { DfListComponent } from './features/entity/df/list/df-list';
import { DfCreateComponent } from './features/entity/df/create/df-create';
import { DfDetailComponent } from './features/entity/df/detail/df-detail';
// Ops screens
import { OpsLayoutComponent } from './features/ops/layout/ops-layout';
import { OpsDashboardComponent } from './features/ops/dashboard/ops-dashboard';
import { OpsBreachingComponent } from './features/ops/breaching/ops-breaching';
import { OpsLookupListsComponent } from './features/ops/lookup-lists/ops-lookup-lists';
import {
  ApplicationsQueueComponent,
  redirectKickstartQueue,
} from './features/ops/applications/applications-queue';
import { DebtorsQueueComponent } from './features/ops/debtors/debtors-queue';
import { DfFacilityQueueComponent } from './features/ops/df/facility-queue/df-facility-queue';
import { DfFacilityOpsDetailComponent } from './features/ops/df/facility-detail/df-facility-ops-detail';
import { DfInvoiceQueueComponent } from './features/ops/df/invoice-queue/df-invoice-queue';
import { DfInvoiceOpsDetailComponent } from './features/ops/df/invoice-detail/df-invoice-ops-detail';
import { PreApplicationQueueComponent } from './features/ops/pre-applications/queue/pre-application-queue';
import { TermsComponent } from './features/legal/terms/terms';
import { PrivacyComponent } from './features/legal/privacy/privacy';
import { StyleGuideComponent } from './features/style-guide/style-guide';
import { SeoData } from './core/seo/seo.service';

const NOINDEX: { seo: SeoData } = { seo: { robots: 'noindex, nofollow' } };

/** `pre-applications/:preAppId` and `pre-applications/:preAppId/:section`, one route. */
export function preApplicationFileMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  const [list, preAppId, section] = segments;
  if (list?.path !== 'pre-applications' || !preAppId || segments.length > 3) return null;
  return { consumed: segments, posParams: section ? { preAppId, section } : { preAppId } };
}

export const routes: Routes = [
  {
    path: '',
    component: LandingComponent,
    canActivate: [guestGuard],
    pathMatch: 'full',
    data: {
      seo: {
        title: 'CapX Flow — Smart Business Funding',
        description:
          'CapX Flow is a smart business funding platform for South African SMEs and brokers. Log in or create your account.',
        canonical: 'https://flow.capx.co.za/',
      } satisfies SeoData,
    },
  },
  {
    path: 'auth',
    children: [
      { path: '', redirectTo: '/login', pathMatch: 'full' },
      {
        path: 'login',
        component: LoginComponent,
        canActivate: [guestGuard],
        data: {
          seo: {
            title: 'Log in — CapX Flow',
            description:
              'Log in to CapX Flow to manage loan applications, review AI-assisted credit decisions, and track funding for your business or clients.',
            canonical: 'https://flow.capx.co.za/auth/login',
          } satisfies SeoData,
        },
      },
      {
        path: 'register',
        component: RegisterComponent,
        canActivate: [guestGuard],
        data: {
          seo: {
            title: 'Sign up — CapX Flow',
            description:
              'Create your CapX Flow account to apply for business finance or originate loans as a broker. Fast onboarding for South African SMEs.',
            canonical: 'https://flow.capx.co.za/auth/register',
          } satisfies SeoData,
        },
      },
      {
        path: 'check-email',
        component: CheckEmailComponent,
        canActivate: [guestGuard],
        data: NOINDEX,
      },
      { path: 'verify-email', component: VerifyEmailComponent, data: NOINDEX },
      {
        path: 'complete-profile',
        component: CompleteProfileComponent,
        canActivate: [authenticatedGuard],
        data: NOINDEX,
      },
      { path: 'reset-password', component: ResetPasswordComponent, data: NOINDEX },
      {
        path: 'reset-password/email-sent',
        component: ResetPasswordEmailSentComponent,
        data: NOINDEX,
      },
      { path: 'reset-password/confirm', component: ResetPasswordConfirmComponent, data: NOINDEX },
    ],
  },
  {
    path: 'legal',
    children: [
      { path: 'terms', component: TermsComponent },
      { path: 'privacy', component: PrivacyComponent },
    ],
  },
  {
    path: 'invitations/accept',
    component: AcceptInvitationComponent,
    data: NOINDEX,
  },
  {
    path: 'agency-invitations/accept',
    component: AcceptAgencyInvitationComponent,
    data: NOINDEX,
  },
  { path: 'dashboard', component: DashboardComponent, canActivate: [authGuard], data: NOINDEX },
  { path: 'profile', component: ProfileComponent, canActivate: [authGuard], data: NOINDEX },
  {
    path: 'agent/dashboard',
    component: AgentDashboardComponent,
    canActivate: [authGuard],
    data: NOINDEX,
  },
  {
    path: 'agent/team',
    component: AgentTeamComponent,
    canActivate: [authGuard],
    data: NOINDEX,
  },
  {
    path: 'agent/clients/new',
    component: AgentOnboardClientComponent,
    canActivate: [authGuard],
    data: NOINDEX,
  },
  {
    path: 'agent/applications/:product/:appId',
    component: AgentApplicationDetailComponent,
    canActivate: [authGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/dashboard',
    component: AdminDashboardComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/users',
    component: AdminUsersComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/agencies',
    component: AdminAgenciesComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/ai-evaluations',
    component: AiEvaluationsComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/ai-evaluations/:id',
    component: AiEvaluationDetailComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/email-approvals',
    component: EmailApprovalsComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/emails',
    component: AdminEmailsComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/feedback',
    component: AdminFeedbackComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    // `short_id` — the reference the reporter is shown and the notification
    // email quotes, so it is what somebody pastes to find a report again.
    path: 'admin/feedback/:id',
    component: FeedbackDetailComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/system-status',
    component: SystemStatusComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/system-status/logs',
    component: SystemLogsComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/system-status/database',
    component: DatabaseBrowserComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/developer-docs',
    component: DeveloperDocsComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/ai-notes',
    loadComponent: () =>
      import('./features/admin/ai-notes/ai-notes-admin').then((m) => m.AiNotesAdminComponent),
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/compliance',
    component: ComplianceComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  {
    path: 'admin/user-journey',
    component: UserJourneyComponent,
    canActivate: [adminGuard],
    data: NOINDEX,
  },
  { path: 'style-guide', component: StyleGuideComponent, canActivate: [authGuard], data: NOINDEX },
  {
    path: 'entity',
    canActivate: [authGuard],
    children: [
      { path: 'create', component: CreateEntityComponent, data: NOINDEX },
      { path: 'deleted', component: DeletedBusinessesComponent, data: NOINDEX },
      { path: ':id/dashboard', component: EntityDashboardComponent, data: NOINDEX },
      { path: ':id/team', component: EntityTeamComponent, data: NOINDEX },
      // The intake wizard keeps its step in the URL (…/new/bank | financials |
      // offers) so the address bar tracks progress and back/forward work; the
      // bare URL stays the canonical entry point and lands on the first step.
      {
        path: ':id/applications/new',
        pathMatch: 'full',
        redirectTo: ':id/applications/new/bank',
      },
      {
        path: ':id/applications/new/:step',
        component: ProductChooserComponent,
        data: NOINDEX,
      },
      // A submitted enquiry is read-only: the wizard above refuses to hydrate one
      // (so the client can always start afresh), which left the dashboard's "View
      // submission" CTA pointing at a blank wizard. This is where it points now.
      {
        path: ':id/applications/submission/:preAppId',
        component: PreApplicationViewComponent,
        data: NOINDEX,
      },
      // A submitted enquiry still ACCEPTS documents (PO ruling 2026-09-22,
      // add-only): this is where the confirmation's and the dashboard's
      // "Add documents" actions land.
      {
        path: ':id/applications/submission/:preAppId/documents',
        component: AddDocumentsComponent,
        data: NOINDEX,
      },
      // Kickstart Cash
      { path: ':id/kickstart', component: KsListComponent, data: NOINDEX },
      // Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application.
      // The guard lets `?draft=<sid>` (a resume) through and sends a fresh start
      // to `:id/applications/new`; see core/applications/direct-applications.ts.
      {
        path: ':id/kickstart/new',
        component: KsCreateComponent,
        canActivate: [directApplicationGuard],
        data: NOINDEX,
      },
      { path: ':id/kickstart/:appId', component: KsDetailComponent, data: NOINDEX },
      // Debtors Finance
      { path: ':id/df', component: DfListComponent, data: NOINDEX },
      // Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application.
      {
        path: ':id/df/new',
        component: DfCreateComponent,
        canActivate: [directApplicationGuard],
        data: NOINDEX,
      },
      { path: ':id/df/:facilityId', component: DfDetailComponent, data: NOINDEX },
    ],
  },
  {
    path: 'ops',
    component: OpsLayoutComponent,
    canActivate: [opsGuard],
    data: NOINDEX,
    children: [
      { path: 'dashboard', component: OpsDashboardComponent, data: NOINDEX },
      { path: 'breaching', component: OpsBreachingComponent, data: NOINDEX },
      { path: 'lookup-lists', component: OpsLookupListsComponent, data: NOINDEX },
      { path: 'applications', component: ApplicationsQueueComponent, data: NOINDEX },
      { path: 'pre-applications', component: PreApplicationQueueComponent, data: NOINDEX },
      {
        // One route for the file and its areas, so moving between areas keeps the page.
        matcher: preApplicationFileMatcher,
        // Lazy: the capture card (FLOW-397) took the initial bundle over its
        // 3.5 MB cap; the page is Ops-only and never on the first paint.
        loadComponent: () =>
          import('./features/ops/pre-applications/detail/pre-application-ops-detail').then(
            (m) => m.PreApplicationOpsDetailComponent,
          ),
        data: NOINDEX,
      },
      { path: 'debtors', component: DebtorsQueueComponent, data: NOINDEX },
      // FLOW-346: the Kickstart queue is the Applications page's first tab.
      { path: 'kickstart', pathMatch: 'full', redirectTo: redirectKickstartQueue },
      // Lazy (FLOW-350): the largest Ops page, loaded when opened, keeps the
      // initial bundle under its 3.5 MB budget.
      {
        path: 'kickstart/:appId',
        loadComponent: () =>
          import('./features/ops/kickstart/detail/ks-ops-detail').then(
            (m) => m.KsOpsDetailComponent,
          ),
        data: NOINDEX,
      },
      { path: 'df/facilities', component: DfFacilityQueueComponent, data: NOINDEX },
      {
        path: 'df/facilities/:facilityId',
        component: DfFacilityOpsDetailComponent,
        data: NOINDEX,
      },
      { path: 'df/invoices', component: DfInvoiceQueueComponent, data: NOINDEX },
      { path: 'df/invoices/:invoiceId', component: DfInvoiceOpsDetailComponent, data: NOINDEX },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: '' },
];
